import React from "react";
import { TemplateFormData } from "../../../views/marketing-pages/templates";
import Textarea from "../../textarea";
import { CharCount, SectionLabel } from "./utiz";

export const MessageBodySection: React.FC<{ formData: TemplateFormData; onChange: (u: Partial<TemplateFormData>) => void }> = ({ formData, onChange }) => {
  const [variableCount, setVariableCount] = React.useState(0);
  const textareaId = React.useId();

  // Calculate number of variables in message body
  React.useEffect(() => {
    const matches = formData.messageBody.match(/{{(\d+)}}/g);
    setVariableCount(matches ? matches.length : 0);
  }, [formData.messageBody]);

  // Helper for message body change
  const handleMessageBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange({ messageBody: e.target.value });
  };

  // Get the next available variable number
  const getNextVariableNumber = () => {
    const matches = formData.messageBody.match(/{{(\d+)}}/g);
    if (!matches) return 1;
    
    const numbers = matches.map(match => {
      const num = match.match(/\d+/);
      return num ? parseInt(num[0]) : 0;
    });
    
    const maxNumber = Math.max(...numbers, 0);
    return maxNumber + 1;
  };

  // Insert variable at the end of the text
  const insertVariable = () => {
    const nextNumber = getNextVariableNumber();
    const variableText = `{{${nextNumber}}}`;
    
    // Get the textarea element by ID
    const textarea = document.getElementById(textareaId) as HTMLTextAreaElement;
    
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentText = formData.messageBody;
      
      // Insert the variable at cursor position
      const newText = currentText.substring(0, start) + variableText + currentText.substring(end);
      onChange({ messageBody: newText });
      
      // Set cursor position after the inserted variable
      setTimeout(() => {
        textarea.focus();
        const newCursorPos = start + variableText.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    } else {
      // Fallback: append to the end
      const newText = formData.messageBody + (formData.messageBody ? ' ' : '') + variableText;
      onChange({ messageBody: newText });
    }
  };

  return (
    <div>
      <SectionLabel label="Message Body" />
      <div className="relative">
        <Textarea 
          id={textareaId}
          value={formData.messageBody} 
          onChange={handleMessageBodyChange} 
          placeholder="Enter your message body... Example: Hello {{1}}, your order {{2}} is ready!"  
        />
        <button 
          type="button" 
          onClick={insertVariable}
          className="absolute bottom-3 right-3 text-xs text-indigo-600 dark:text-indigo-400 font-medium border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-500/10 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 rounded px-2.5 py-1 transition-colors z-10"
        >
          Add variable
        </button>
      </div>
      <div className="flex justify-between items-center mt-1">
        <CharCount current={formData.messageBody.length} max={1024} />
        <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">
          {variableCount} variable{variableCount !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
};